import React, { useEffect, useContext, useState, useMemo } from "react";
import { ProductsContext } from "../context/ProductsContext";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";
import { brandModelData } from "../context/brandModelData";

const Shop = () => {
  const { products = [], fetchProducts, loading, error } =
    useContext(ProductsContext);

  const { lang } = useLanguage();
  const navigate = useNavigate();

  const ALL = "All";
  const PRODUCTS_PER_PAGE = 16;

  const [currentPage, setCurrentPage] = useState(1);

  const [selectedCategory, setSelectedCategory] = useState(ALL);
  const [selectedCondition, setSelectedCondition] = useState(ALL);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 2000]);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // ================= FETCH =================
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // ================= RESET PAGE =================
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedCategory,
    selectedCondition,
    selectedBrands,
    selectedColors,
    priceRange,
    search,
  ]);

  // ================= TRANSLATIONS =================
  const t = {
    sq: {
      search: "Kërko produkt...",
      price: "Çmimi",
      max: "Maksimumi",
      category: "Kategoria",
      brand: "Marka",
      color: "Ngjyra",
      products: "Produktet",
      reset: "Pastro filtrat",
      view: "Shiko",
      filters: "Filtrat",
      closeFilters: "Mbyll Filtrat",
      noResults: "Duke u ngarkuar...",
    },
    en: {
      search: "Search product...",
      price: "Price",
      max: "Max",
      category: "Category",
      brand: "Brand",
      color: "Color",
      products: "Products",
      reset: "Reset filters",
      view: "View",
      filters: "Filters",
      closeFilters: "Close Filters",
      noResults: "Loading...",
    },
  };

  const text = t[lang] || t.en;

  // ================= SAFE PRODUCTS =================
  const safeProducts = Array.isArray(products) ? products : [];

  // ================= COLORS =================
  const colorMap = {
    Zeze: "#000000",
    Bardhe: "#ffffff",
    Gri: "#9ca3af",
    Blu: "#2563eb",
    Jeshile: "#16a34a",
    Kuqe: "#dc2626",
    Verdhe: "#eab308",
    Vjollce: "#a855f7",
    Rozë: "#ec4899",
    Ari: "#d4af37",
    Argjend: "#c0c0c0",
  };

  const colorList = Object.keys(colorMap);

  // ================= CATEGORIES =================
  const categoryList = [
    ALL,
    "Telefona",
    "Laptop",
    "Tablete",
    "Kufje",
    "Smartwatch",
    "Aksesore",
  ];

  // ================= BRANDS =================
  const brandList = useMemo(() => {
    if (selectedCategory === ALL) {
      return [
        ...new Set(
          Object.values(brandModelData || {}).flatMap((c) =>
            Object.keys(c || {})
          )
        ),
      ];
    }
    return Object.keys(brandModelData?.[selectedCategory] || {});
  }, [selectedCategory]);

  // ================= IMAGE =================
  const getImage = (img) => {
    if (!img) return "/placeholder.png";
    if (Array.isArray(img)) img = img[0];
    if (typeof img === "string" && img.includes(",")) {
      img = img.split(",")[0];
    }
    return `${API_BASE_URL}/uploads/${String(img).trim()}`;
  };

  // ================= FILTER ENGINE =================
  const filteredProducts = useMemo(() => {
    const norm = (v) => (v || "").toLowerCase().trim();

    return safeProducts.filter((p) => {
      const matchCategory =
        selectedCategory === ALL || p.category === selectedCategory;

      const matchCondition =
        selectedCondition === ALL ||
        norm(p.product_condition) === norm(selectedCondition);

      const matchBrand =
        selectedBrands.length === 0 || selectedBrands.includes(p.brand);

      const matchColor =
        selectedColors.length === 0 || selectedColors.includes(p.color);

      const matchPrice =
        Number(p.price) >= priceRange[0] &&
        Number(p.price) <= priceRange[1];

      const matchSearch =
        !search ||
        p.name?.toLowerCase().includes(search.toLowerCase());

      return (
        matchCategory &&
        matchCondition &&
        matchBrand &&
        matchColor &&
        matchPrice &&
        matchSearch
      );
    });
  }, [
    safeProducts,
    selectedCategory,
    selectedCondition,
    selectedBrands,
    selectedColors,
    priceRange,
    search,
  ]);

  // ================= PAGINATION =================
  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE)
  );

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  // ================= LOADING / ERROR =================
  if (loading) {
    return <div className="p-10 text-center">{text.noResults}</div>;
  }

  if (error) {
    return <div className="p-10 text-center text-red-500">{error}</div>;
  }

  // ================= UI =================
  return (
    <div className="bg-gray-100 min-h-screen p-4">
          <div className="md:hidden mb-4">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium"
      >
          {showFilters ? text.closeFilters : text.filters}
      </button>
    </div>
     <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-6">

        {/* ================= FILTERS ================= */}
      <aside
                  className={`
                    bg-white p-4 rounded shadow h-fit self-start
                    md:w-72
                    ${showFilters ? "block" : "hidden"}
                    md:block
                  `}
                >

          <input
            className="w-full border p-2 rounded mb-3"
            placeholder={text.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

                    {/* PRICE */}
          <div className="mb-5">
            <h3 className="font-semibold mb-2">{text.price}</h3>

            <input
              type="range"
              min="0"
              max="2000"
              value={priceRange[1]}
              onChange={(e) =>
                setPriceRange([0, Number(e.target.value)])
              }
              className="w-full"
            />
            <div className="flex justify-between text-xs mt-2">
              <span>{text.max}</span>
                <span className="font-bold">
                €{Number(priceRange[1]).toLocaleString()}
              </span>
            </div>
          </div>

          {/* CATEGORY */}
          <h3 className="font-bold mt-4">{text.category}</h3>
          {categoryList.map((c) => (
            <label key={c} className="block text-sm">
              <input
                type="radio"
                checked={selectedCategory === c}
                onChange={() => setSelectedCategory(c)}
              />
              {" "}{c}
            </label>
          ))}

          {/* BRAND */}
          <h3 className="font-bold mt-4">{text.brand}</h3>
          <div className="max-h-40 overflow-auto">
            {brandList.map((b) => (
              <label key={b} className="block text-sm">
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(b)}
                  onChange={() =>
                    setSelectedBrands((prev) =>
                      prev.includes(b)
                        ? prev.filter((x) => x !== b)
                        : [...prev, b]
                    )
                  }
                />
                {" "}{b}
              </label>
            ))}
          </div>

          {/* COLORS */}
          <h3 className="font-bold mt-4">{text.color}</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {colorList.map((c) => (
              <div
                key={c}
                onClick={() =>
                  setSelectedColors((prev) =>
                    prev.includes(c)
                      ? prev.filter((x) => x !== c)
                      : [...prev, c]
                  )
                }
                className={`w-6 h-6 rounded-full border cursor-pointer ${
                  selectedColors.includes(c)
                    ? "ring-2 ring-purple-600"
                    : ""
                }`}
                style={{ backgroundColor: colorMap[c] }}
              />
            ))}
          </div>

          {/* RESET */}
          <button
            onClick={() => {
              setSelectedCategory(ALL);
              setSelectedCondition(ALL);
              setSelectedBrands([]);
              setSelectedColors([]);
              setPriceRange([0, 2000]);
              setSearch("");
            }}
            className="mt-5 w-full bg-gray-200 p-2 rounded"
          >
            {text.reset}
          </button>
        </aside>

        {/* ================= PRODUCTS ================= */}
        <div className="flex-1">

          <h2 className="text-xl font-bold mb-4">
            {text.products} ({filteredProducts.length})
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {paginatedProducts.map((p) => (
              <div
                key={p.id}
                onClick={() =>
                  navigate(
                    `/product/${encodeURIComponent(
                      p.brand
                    )}/${encodeURIComponent(p.model)}`
                  )
                }
                className="bg-white p-3 rounded shadow hover:shadow-lg cursor-pointer"
              >
                              <img
                  src={getImage(p.images)}
                  className="h-28 md:h-40 w-full object-contain"
                />

              <h3 className="text-xs md:text-sm font-semibold mt-2 line-clamp-2">
                  {p.name}
                </h3>

                <p className="font-bold">{p.price} €</p>
                    
                     <button
        onClick={(e) => {
          
          navigate(
            `/product/${encodeURIComponent(p.brand)}/${encodeURIComponent(
              p.model
            )}`
          );
        }}
        className="mt-2 bg-green-600 text-white text-xs md:text-sm py-2 rounded-md hover:bg-green-700 transition w-full"
      >
        Bli Tani
      </button>


              </div>
            ))}
          </div>

          {/* PAGINATION */}
          <div className="flex justify-center gap-2 mt-10">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ←
            </button>

            <span>
              {currentPage} / {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;